import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLocationDot, faPhone } from '@fortawesome/free-solid-svg-icons';
import { scrollToElement } from '../../utils/helpers';
import { localizePath, splitLanguageFromPath } from '../../utils/language';
import ShareButtons from '../ShareButtons';
import './Footer.css';

const PRIMARY_PHONE_DISPLAY = '+998 77 255 44 40';
const SECONDARY_PHONE_DISPLAY = '+998 77 266 44 40';
const PRIMARY_PHONE_RAW = '998772554440';
const SECONDARY_PHONE_RAW = '998772664440';
const TELEGRAM_URL = 'https://t.me/podiatechuz';
const INSTAGRAM_URL = 'https://www.instagram.com/podiatech.uz?igsh=N2hyeDZvdHAyeHl6';
const FACEBOOK_URL = 'https://www.facebook.com/share/17NBkys7qi/?mibextid=wwXIfr';

const Footer = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const isHomePage = splitLanguageFromPath(location.pathname).path === '/';
    const homePath = localizePath(i18n.language, '/');

    const quickLinks = [
        { key: 'home', label: t('nav.home'), href: '#hero' },
        { key: 'about', label: t('nav.about'), href: '#insoles' },
        { key: 'products', label: t('nav.products'), href: '#products' },
        { key: 'faq', label: t('nav.faq'), href: '#faq' },
    ];

    const topicLinks = [
        { key: 'yassi-oyoq', slug: 'yassi-oyoq' },
        { key: 'tovon-tikani', slug: 'tovon-tikani' },
        { key: 'bolalar-uchun', slug: 'bolalar-uchun' },
        { key: 'sport-uchun', slug: 'sport-uchun' },
        { key: 'buyurtma-asosida', slug: 'buyurtma-asosida' }
    ];

    const services = [
        { key: 'diagnosis', label: t('products.items.diagnosis_3d.title'), href: '#products' },
        { key: 'consultation', label: t('products.items.consultation.title'), href: '#products' },
        { key: 'custom', label: t('products.items.custom_insoles.title'), href: '#products' },
    ];
    const contactItems = [
        {
            key: 'phone',
            icon: faPhone,
            color: 'primary',
            phoneNumbers: [
                { display: PRIMARY_PHONE_DISPLAY, raw: PRIMARY_PHONE_RAW },
                { display: SECONDARY_PHONE_DISPLAY, raw: SECONDARY_PHONE_RAW }
            ]
        },
        { key: 'email', icon: faEnvelope, color: 'accent', value: 'podiatechuz@gmail.com', href: 'mailto:podiatechuz@gmail.com' },
        { key: 'address', icon: faLocationDot, color: 'secondary', value: t('contacts_info.address_value') },
    ];

    const handleLinkClick = (e, href) => {
        e.preventDefault();
        const id = href.replace('#', '');
        if (isHomePage) {
            scrollToElement(id);
        } else {
            navigate({ pathname: homePath, hash: `#${id}` });
        }
    };

    return (
        <footer className="footer">
            <div className="container">
                <div className="footer-content">
                    {/* Brand Column */}
                    <div className="footer-brand">
                        <Link to={homePath} className="footer-logo" onClick={(e) => handleLinkClick(e, '#hero')}>
                            <img src="/logo.jpg" alt="Logo" />
                        </Link>
                        <p className="footer-description">
                            {t('footer.description')}
                        </p>
                        <div className="footer-social">
                            <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.223-.535.223l.19-2.712 4.94-4.465c.215-.19-.047-.295-.335-.105l-6.107 3.843-2.63-.823c-.572-.18-.582-.572.12-.847l10.27-3.96c.477-.172.896.116.587.874z" />
                                </svg>
                            </a>
                            <a href={`https://wa.me/${PRIMARY_PHONE_RAW}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                            </a>
                            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                </svg>
                            </a>
                            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 6.019 4.388 11.009 10.125 11.927v-8.437H7.078v-3.49h3.047V9.41c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.082 24 18.092 24 12.073z" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="footer-column">
                        <h3 className="footer-title">{t('footer.quick_links')}</h3>
                        <ul className="footer-links">
                            {quickLinks.map((link) => (
                                <li key={link.key}>
                                    <a href={link.href} onClick={(e) => handleLinkClick(e, link.href)}>
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                            {/* Статическая страница вне SPA — обычный переход, не Link */}
                            <li>
                                <a href={localizePath(i18n.language, '/filiallar')}>
                                    {t('nav.branches')}
                                </a>
                            </li>
                            <li>
                                <a href={localizePath(i18n.language, '/ortopedik-stelkalar')}>
                                    {t('topics.hub')}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Services */}
                    <div className="footer-column">
                        <h3 className="footer-title">{t('footer.services')}</h3>
                        <ul className="footer-links">
                            {services.map((link) => (
                                <li key={link.key}>
                                    <a href={link.href} onClick={(e) => handleLinkClick(e, link.href)}>
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                            {/* Статические страницы вне SPA — обычные переходы */}
                            {topicLinks.map((topic) => (
                                <li key={topic.key}>
                                    <a href={localizePath(i18n.language, `/ortopedik-stelkalar/${topic.slug}`)}>
                                        {t(`topics.${topic.key}`)}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div className="footer-column">
                        <h3 className="footer-title">{t('footer.contact')}</h3>
                        <ul className="footer-contact">
                            {contactItems.map((item) => (
                                <li key={item.key}>
                                    <span
                                        className={`contact-icon contact-icon-${item.color}`}
                                        aria-hidden="true"
                                    >
                                        <FontAwesomeIcon icon={item.icon} />
                                    </span>
                                    {item.phoneNumbers ? (
                                        <span className="footer-contact-multi">
                                            {item.phoneNumbers.map((phone, index) => (
                                                <span key={phone.raw} className="footer-contact-line">
                                                    <a href={`tel:+${phone.raw}`}>{phone.display}</a>
                                                    {index < item.phoneNumbers.length - 1 ? <br /> : null}
                                                </span>
                                            ))}
                                        </span>
                                    ) : item.href ? (
                                        <a href={item.href}>{item.value}</a>
                                    ) : (
                                        <span>{item.value}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Share Buttons */}
                <div className="footer-share">
                    <ShareButtons />
                </div>

                {/* Bottom Bar */}
                <div className="footer-bottom">
                    <p className="copyright">{t('footer.copyright')}</p>
                    <p className="footer-credit">
                        {t('footer.developed_by')}{' '}
                        <a
                            href="https://uc-coding.uz"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('footer.developer_name')}
                        </a>
                    </p>
                    <div className="footer-legal">
                        <a href="#privacy">{t('footer.privacy')}</a>
                        <a href="#terms">{t('footer.terms')}</a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
